import React, { FC, useCallback, useMemo, useState } from 'react';
import { Dialog } from '@mui/material';
import Dropzone, { FileRejection } from 'react-dropzone';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import API from 'api/API';
import { DeviceStatus, deviceStatus } from 'api/Responses/DeviceResponses';
import AsyncImage from 'components/AsyncImage/AsyncImage';
import BackButton from 'components/BackButton/BackButton';
import Button from 'components/Button';
import Checkbox from 'components/Checkbox/Checkbox';
import ConfirmModal from 'components/ConfirmModal/ConfirmModal';
import DuplicateImageModal from 'components/DuplicateImageModal/DuplicateImageModal';
import FormControlLabel from 'components/FormControl/FormControlLabel';
import InputField from 'components/InputField';
import Label from 'components/Label/Label';
import Page from 'components/Page';
import PageSpinner from 'components/PageSpinner/PageSpinner';
import PdfPreview from 'components/PdfPreview/PdfPreview';
import ViewEnlargedImageModal from 'components/ViewEnlargedImageModal/ViewEnlargedImageModal';
import { useLanguage } from 'context/AppContext';
import useDevice from 'hooks/useDevice';
import { deviceActions } from 'reducers/DeviceReducer/deviceActions';
import { FileService, ImageService } from 'services/FileService';
import { useUser } from 'store/user/useUser';
import { StorageType } from 'utils/helpers/image.helpers';
import { isReviewer } from 'utils/helpers/permissions.helpers';
import * as Functions from '../DevicePage.functions';
import * as Styles from '../DevicePage.styles';

// Match params on the page
type DevicePageParams = {
  code: string;
};

const EditDevicePage: FC = () => {
  const { code = '' } = useParams<DevicePageParams>();
  const navigate = useNavigate();
  const user = useUser();
  const [language] = useLanguage();
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [loadingAction, setLoadingAction] = useState(false);
  const [duplicateInfo, setDuplicateInfo] = useState<{ url: string; file: File } | null>(null);
  const [isEnlargedImageModalOpen, setIsEnlargedImageModalOpen] = useState<boolean>(false);

  // Load device on page mount
  const { loading: loadingPage, device, dispatchDevice } = useDevice(code, false, true);

  // Make sure is draft
  const isDraft = device.status === DeviceStatus.DRAFT;

  // Check if user is owner
  const isOwner = user.isAuthenticated && device.owned_by === user.user_id;

  // Return if draft exists and owned by someone else
  if (isDraft && isOwner && device.id) {
    navigate(`/device/${device.id}`, { replace: true });
  }

  // Device actions
  const handleAction = useCallback(
    async (action: Functions.DeviceAction) => {
      setLoadingAction(true);
      try {
        Functions.deviceValidate(device);
        await Functions.deviceActionSwitch(action, device);
        action !== 'save' && navigate('/drafts');
      } catch (error) {
        API.errorHandler(error);
      }
      setLoadingAction(false);
    },
    [device, navigate],
  );

  const saveImageToDatabase = useCallback(
    async (imageURL: string, imageFile: File) => {
      // Save the image directly to firebase
      await ImageService.saveImage(imageURL, imageFile);

      // Update the device with the new image reference
      await API.device.update({ ...device, image: imageURL });

      // Dispatch the change
      dispatchDevice(deviceActions.image(imageURL));

      // Success notification
      toast.success(language.DevicePage.uploadImageSuccess);
    },
    [device, dispatchDevice, language],
  );

  // Device image drop handler
  const handleImageDrop = useCallback(
    async (acceptedFiles: File[], error: FileRejection[]) => {
      setLoadingAction(true);
      try {
        // Throw an error if the dropper returns one
        if (error.length > 0 || !user.isAuthenticated) {
          throw new Error(error[0].errors[0].message);
        }
        // Get the new filename for the image
        const imageURL = `${device.tenant}/part_reference_images/${user.region}_${process.env.REACT_APP_AUTH0_ORGANIZATION}_${device.id}_${acceptedFiles[0].name}`;

        // Check if the image already exists in the database
        const imageExists = await ImageService.doesImageExistInDatabase(imageURL);

        if (imageExists) {
          setDuplicateInfo({ url: imageURL, file: acceptedFiles[0] });
        } else {
          await saveImageToDatabase(imageURL, acceptedFiles[0]);
        }
      } catch (error) {
        API.errorHandler(error);
      }
      setLoadingAction(false);
    },
    [device.id, device.tenant, saveImageToDatabase, user],
  );

  const replaceExistingImageFile = useCallback(async () => {
    if (duplicateInfo) {
      await saveImageToDatabase(duplicateInfo?.url, duplicateInfo?.file);
      setDuplicateInfo(null);
    }
  }, [duplicateInfo, saveImageToDatabase]);

  const handleInspectionManualDrop = useCallback(
    async (acceptedFiles: File[], error: FileRejection[]) => {
      setLoadingAction(true);
      try {
        // Throw an error if the dropper returns one
        if (error.length > 0) {
          throw new Error(error[0].errors[0].message);
        }
        // Save the document directly to firebase
        const docUrl = Functions.generateFilePath(device.id, acceptedFiles[0].name);
        // Get the new filename for the document
        await FileService.saveFile(docUrl, acceptedFiles[0]);
        // Update the device with the new document reference
        await API.device.update({
          ...device,
          inspection_manual: acceptedFiles[0],
          language_metadata: { ...device.language_metadata, manual_english: docUrl },
        });
        // Dispatch the change
        dispatchDevice(deviceActions.inspectionManual(acceptedFiles[0]));
        dispatchDevice(deviceActions.inspectionManualUrl(docUrl));
        // Success notification
        toast.success(language.DevicePage.uploadDocSuccess);
      } catch (error) {
        API.errorHandler(error);
      }
      setLoadingAction(false);
    },
    [device, dispatchDevice, language.DevicePage.uploadDocSuccess],
  );

  const additionalInspectionText = useMemo(
    () =>
      // Try the new inspection text field
      (device.test_metadata?.additional_inspection_english ||
        // Otherwise try the old inspection text field
        device?.inspection_text) ??
      // If no values are available, keep the value controlled with an empty string (https://reactjs.org/docs/forms.html#controlled-components)
      '',
    [device],
  );

  return (
    <Page>
      {loadingPage ? (
        <PageSpinner />
      ) : (
        <Styles.Container>
          {/* Header */}
          <Styles.DevicePageRowAlignCenter>
            <Styles.HeaderContainer>
              <BackButton onClick={() => navigate(-1)} />
              <h2>{device.description}</h2>
            </Styles.HeaderContainer>
            <Styles.ButtonContainer>
              {isReviewer(user) ? (
                <Button onClick={() => handleAction('merge')}>{language.DraftActions.merge}</Button>
              ) : (
                <Button onClick={() => handleAction('submit')}>{language.DraftActions.submit}</Button>
              )}
              <Button onClick={() => handleAction('save')}>{language.DraftActions.save}</Button>
              <Button onClick={() => setConfirmDelete(true)}>{language.DraftActions.delete}</Button>
            </Styles.ButtonContainer>
          </Styles.DevicePageRowAlignCenter>

          <Styles.FormElement width={10}>
            <Label text={language.DevicePage.status}>
              <Styles.Status status={deviceStatus[device.status]}>{language.DeviceStatus[device.status]}</Styles.Status>
            </Label>
          </Styles.FormElement>

          {/* Code */}
          <Styles.FormElement width={20}>
            <Label text={language.DevicePage.code}>
              <InputField
                variant="outlined"
                fullWidth
                value={device.id}
                onChange={e => dispatchDevice(deviceActions.id(e.target.value))}
              />
            </Label>
          </Styles.FormElement>

          {/* Number of Parts */}
          <Styles.FormElement width={20}>
            <Label text={language.DevicePage.numParts}>
              <InputField
                type="number"
                variant="outlined"
                fullWidth
                value={device.num_parts}
                onChange={e => dispatchDevice(deviceActions.numParts(e.target.value))}
              />
            </Label>
          </Styles.FormElement>

          {/* Description */}
          <Styles.FormElement width={50}>
            <Label text={language.DevicePage.description}>
              <InputField
                variant="outlined"
                fullWidth
                value={device.description}
                onChange={e => dispatchDevice(deviceActions.description(e.target.value))}
              />
            </Label>
          </Styles.FormElement>

          {/* Japanese Description */}
          <Styles.FormElement width={50}>
            <Label text={language.DevicePage.japaneseDescription}>
              <InputField
                variant="outlined"
                fullWidth
                value={device.language_metadata?.description_japanese}
                onChange={e => dispatchDevice(deviceActions.japaneseDescription(e.target.value))}
              />
            </Label>
          </Styles.FormElement>

          {/* Checkboxes */}
          <Styles.FormCheckboxGroup>
            <Styles.FormElement width={12}>
              {/* Cannulated */}
              <FormControlLabel
                control={
                  <Checkbox
                    checked={!!device.cannulated}
                    onChange={() => dispatchDevice(deviceActions.cannulated(!device.cannulated))}
                  />
                }
                label={language.DevicePage.cannulated}
              />

              {/* Debris Inspection */}
              <FormControlLabel
                control={
                  <Checkbox
                    checked={!!device.test_metadata?.debris_test}
                    onChange={() => dispatchDevice(deviceActions.debrisInspection(!device.test_metadata?.debris_test))}
                  />
                }
                label={language.DeviceTests.debrisInspection}
              />

              {/* Function Test */}
              <FormControlLabel
                control={
                  <Checkbox
                    checked={!!device.test_metadata?.functional_test}
                    onChange={() => dispatchDevice(deviceActions.functionTest(!device.test_metadata?.functional_test))}
                  />
                }
                label={language.DeviceTests.functionTest}
              />

              {/* Torque Test */}
              <FormControlLabel
                control={
                  <Checkbox
                    checked={!!device.test_metadata?.torque_test}
                    onChange={() => dispatchDevice(deviceActions.torqueTest(!device.test_metadata?.torque_test))}
                  />
                }
                label={language.DeviceTests.torqueTest}
              />

              {/* Prone to Debris */}
              <FormControlLabel
                control={
                  <Checkbox
                    checked={!!device.prone_to_debris}
                    onChange={() => dispatchDevice(deviceActions.proneToDebris(!device.prone_to_debris))}
                  />
                }
                label={language.DeviceTests.proneToDebris}
              />
            </Styles.FormElement>
          </Styles.FormCheckboxGroup>

          {/* Additional Inspection */}
          <Styles.FormElement width={50}>
            <Label text={language.DevicePage.additionalInspection}>
              <InputField
                variant="outlined"
                fullWidth
                value={additionalInspectionText}
                onChange={e => dispatchDevice(deviceActions.additionalInspection(e.target.value))}
              />
            </Label>
          </Styles.FormElement>

          {/* Japanese Additional Inspection */}
          <Styles.FormElement width={50}>
            <Label text={language.DevicePage.japaneseAdditionalInspection}>
              <InputField
                variant="outlined"
                fullWidth
                value={device.test_metadata?.additional_inspection_japanese}
                onChange={e => dispatchDevice(deviceActions.japaneseAdditionalInspection(e.target.value))}
              />
            </Label>
          </Styles.FormElement>

          {/* Image */}
          <Styles.FormElement width={50}>
            <Label text="Part Image">
              <Styles.UploadContainer>
                <AsyncImage
                  alt={device.description}
                  src={device.image}
                  imgWidth={19}
                  imgHeight={16}
                  isEnlargedImageAvailable
                  storageType={StorageType.FIREBASE}
                />
                <Dropzone onDrop={handleImageDrop} accept="image/*" maxFiles={1}>
                  {({ getRootProps, getInputProps }) => (
                    <Styles.MarginLeftUploadContainer>
                      <Styles.FileUploadRoot {...getRootProps()}>
                        <input {...getInputProps()} />
                        <p>{language.DevicePage.image}</p>
                      </Styles.FileUploadRoot>
                      <Styles.FileUploadButton>
                        <Button fullWidth onClick={() => toast.info('Coming soon')}>
                          {language.DevicePage.existingImage}
                        </Button>
                      </Styles.FileUploadButton>
                    </Styles.MarginLeftUploadContainer>
                  )}
                </Dropzone>
              </Styles.UploadContainer>
            </Label>
          </Styles.FormElement>

          {/* Inspection Manual */}
          <Styles.FormElement width={50}>
            <Label text="Inspection Manual">
              <Styles.UploadContainer>
                <PdfPreview
                  firebase
                  file={device.inspection_manual}
                  deviceId={device.id}
                  fileUrl={device.language_metadata?.manual_english ?? undefined}
                />
                <Dropzone onDrop={handleInspectionManualDrop} accept="application/pdf,image/*" maxFiles={1}>
                  {({ getRootProps, getInputProps }) => (
                    <Styles.MarginLeftUploadContainer>
                      <Styles.FileUploadRoot {...getRootProps()}>
                        <input {...getInputProps()} />
                        <p>Drag File to Upload New Inspection Documents</p>
                      </Styles.FileUploadRoot>
                    </Styles.MarginLeftUploadContainer>
                  )}
                </Dropzone>
              </Styles.UploadContainer>
            </Label>
          </Styles.FormElement>

          <ConfirmModal
            open={confirmDelete}
            close={() => setConfirmDelete(false)}
            confirm={() => handleAction('delete')}
            confirmText={language.ConfirmModal.device}
            buttonText={language.ConfirmModal.delete}
          />
          <ViewEnlargedImageModal
            modalOpen={isEnlargedImageModalOpen}
            setClosed={() => setIsEnlargedImageModalOpen(false)}
            alt={device.id}
            src={device.image}
          />
          <Dialog open={loadingAction}>
            <PageSpinner />
          </Dialog>
          <DuplicateImageModal
            open={duplicateInfo !== null}
            onClose={() => setDuplicateInfo(null)}
            onCancel={() => setDuplicateInfo(null)}
            onReplaceImage={() => replaceExistingImageFile()}
          />
        </Styles.Container>
      )}
    </Page>
  );
};

export default EditDevicePage;
